import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, TextField, Button, Alert, CircularProgress, Stepper, Step, StepLabel,
} from '@mui/material';
import { authApi } from '../api/services';

// Simple QR code generator using a public API (no dependency needed)
function QrCode({ value, size = 200 }: { value: string; size?: number }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=121829&color=E8ECF4&format=svg`;
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', my: 3,
      '& img': { borderRadius: 2, border: '2px solid rgba(255,255,255,0.1)' }
    }}>
      <img src={url} alt="Scan this QR code with your authenticator app" width={size} height={size} />
    </Box>
  );
}

export default function MfaSetupPage() {
  const [step, setStep] = useState(0); // 0=loading, 1=show QR, 2=verify, 3=done
  const [qrUri, setQrUri] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setupMfa();
  }, []);

  const setupMfa = async () => {
    try {
      const data = await authApi.setupMfa();
      setQrUri(data.authenticatorUri || (data as any).qrUri || '');
      setManualKey(data.sharedKey || (data as any).key || (data as any).authenticatorKey || '');
      setStep(1);
    } catch (err: any) {
      setError('Failed to initialise MFA setup. Please try again.');
    }
  };

  const verifyAndEnable = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await authApi.enableMfa(code);
      setRecoveryCodes(result.recoveryCodes || []);
      setStep(3);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Set Up Multi-Factor Authentication
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Protect your account with an authenticator app like Microsoft Authenticator, Google Authenticator, or Authy.
      </Typography>

      <Card sx={{ p: 4, maxWidth: 600, bgcolor: '#121829' }}>
        <Stepper activeStep={step === 0 ? 0 : step - 1} sx={{ mb: 4 }}>
          <Step><StepLabel>Scan QR Code</StepLabel></Step>
          <Step><StepLabel>Verify Code</StepLabel></Step>
          <Step><StepLabel>Save Recovery Codes</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* Step 0: Loading */}
        {step === 0 && (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>Preparing MFA setup...</Typography>
          </Box>
        )}

        {/* Step 1: Show QR Code */}
        {step === 1 && (
          <>
            <Typography variant="body1" mb={1}>
              Scan this QR code with your authenticator app:
            </Typography>

            {qrUri && <QrCode value={qrUri} size={220} />}

            <Typography variant="body2" color="text.secondary" mb={1}>
              Can't scan? Enter this key manually:
            </Typography>
            <Box sx={{
              p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)',
              fontFamily: 'monospace', fontSize: 14, letterSpacing: '0.15em',
              textAlign: 'center', wordBreak: 'break-all', color: '#6C9BFF',
              border: '1px solid rgba(108,155,255,0.2)',
            }}>
              {manualKey}
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={() => setStep(2)}
              sx={{
                py: 1.5, borderRadius: 2,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
              }}
            >
              I've scanned the code — Next
            </Button>
          </>
        )}

        {/* Step 2: Verify */}
        {step === 2 && (
          <>
            <Typography variant="body1" mb={3}>
              Enter the 6-digit code from your authenticator app to verify setup:
            </Typography>

            <TextField
              label="6-digit code"
              fullWidth
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ maxLength: 6, style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: 24, fontWeight: 600 } }}
              sx={{ mb: 3 }}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && verifyAndEnable()}
              InputProps={{
                sx: { borderRadius: 2, background: 'rgba(255,255,255,0.03)' }
              }}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={verifyAndEnable}
              disabled={loading || code.length !== 6}
              sx={{
                py: 1.5, borderRadius: 2,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify & Enable MFA'}
            </Button>

            <Button
              fullWidth
              onClick={() => setStep(1)}
              sx={{ mt: 1, color: 'text.secondary' }}
            >
              Back to QR code
            </Button>
          </>
        )}

        {/* Step 3: Recovery Codes */}
        {step === 3 && (
          <>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              MFA is now enabled on your account.
            </Alert>

            <Typography variant="body1" fontWeight={600} mb={2}>
              Save your recovery codes
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              If you lose access to your authenticator app, you can use these one-time codes to sign in.
              Store them somewhere safe — you won't be able to see them again.
            </Typography>

            <Box sx={{
              p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              {recoveryCodes.map((code, i) => (
                <Typography key={i} sx={{ fontFamily: 'monospace', fontSize: 14, py: 0.3, color: '#69F0AE' }}>
                  {code}
                </Typography>
              ))}
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                navigator.clipboard.writeText(recoveryCodes.join('\n'));
              }}
              sx={{ mb: 2, py: 1.2, borderRadius: 2 }}
            >
              Copy Recovery Codes
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                // Clear session to force re-auth with updated MFA status
                sessionStorage.clear();
                window.location.href = '/';
              }}
              sx={{ py: 1.2, borderRadius: 2 }}
            >
              Done — Sign in again
            </Button>
          </>
        )}
      </Card>
    </Box>
  );
}
